import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { Button, Card, Tag, Typography } from 'antd'
import {
  FlagNode,
  OutcomeNode,
  OutcomeStatus,
} from 'features/libra/ontology/ontologyFieldTypes'
import { FC } from 'react'
import styled from 'styled-components'

const { Title, Text } = Typography

interface OutcomeNodeRendererProps {
  node: OutcomeNode
  collectedFlags: string[]
  flagNodes: Record<string, FlagNode>
  onReset: () => void
}

const OUTCOME_ICONS: Record<OutcomeStatus, React.ReactNode> = {
  [OutcomeStatus.SUCCESS]: <CheckCircleOutlined />,
  [OutcomeStatus.ERROR]: <CloseCircleOutlined />,
  [OutcomeStatus.WARNING]: <WarningOutlined />,
  [OutcomeStatus.INFO]: <InfoCircleOutlined />,
}

const FLAG_ICONS: Record<'info' | 'warning' | 'error', React.ReactNode> = {
  error: <ExclamationCircleOutlined />,
  warning: <WarningOutlined />,
  info: <InfoCircleOutlined />,
}

export const OutcomeNodeRenderer: FC<OutcomeNodeRendererProps> = ({
  node,
  collectedFlags,
  flagNodes,
  onReset,
}) => {
  const status = node.status ?? OutcomeStatus.SUCCESS
  // Use green/positive styling for success and info, red/negative for error and warning
  const isPositive =
    status === OutcomeStatus.SUCCESS || status === OutcomeStatus.INFO

  const icon = OUTCOME_ICONS[status]

  return (
    <Container>
      <ResultCard $isPositive={isPositive}>
        <ResultIcon $isPositive={isPositive}>{icon}</ResultIcon>
        <ResultTitle level={3}>{node.name}</ResultTitle>
        {node.description && (
          <ResultDescription>{node.description}</ResultDescription>
        )}
      </ResultCard>

      {collectedFlags.length > 0 && (
        <FlagsSection>
          <FlagsTitle level={5}>Flagged Considerations</FlagsTitle>
          <FlagsList>
            {collectedFlags.map((flagId) => {
              const flagNode = flagNodes[flagId]
              if (!flagNode) return null
              return (
                <FlagItem key={flagId}>
                  <FlagIcon $severity={flagNode.severity}>
                    {FLAG_ICONS[flagNode.severity]}
                  </FlagIcon>
                  <FlagContent>
                    <FlagName>{flagNode.name}</FlagName>
                    <SeverityTag $severity={flagNode.severity}>
                      {flagNode.severity}
                    </SeverityTag>
                  </FlagContent>
                </FlagItem>
              )
            })}
          </FlagsList>
        </FlagsSection>
      )}

      <ResetButton type='default' icon={<ReloadOutlined />} onClick={onReset}>
        Start Over
      </ResetButton>
    </Container>
  )
}

const Container = styled.div`
  max-width: 600px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
`

const ResultCard = styled(Card)<{ $isPositive: boolean }>`
  width: 100%;
  text-align: center;
  border: 2px solid
    ${({ $isPositive }) => ($isPositive ? '#52c41a' : '#ff4d4f')};
  background: ${({ $isPositive }) =>
    $isPositive ? 'rgba(82, 196, 26, 0.05)' : 'rgba(255, 77, 79, 0.05)'};
`

const ResultIcon = styled.div<{ $isPositive: boolean }>`
  font-size: 48px;
  color: ${({ $isPositive }) => ($isPositive ? '#52c41a' : '#ff4d4f')};
  margin-bottom: 1rem;
`

const ResultTitle = styled(Title)`
  margin-bottom: 0.5rem !important;
`

const ResultDescription = styled(Text)`
  font-size: 14px;
  color: rgba(0, 0, 0, 0.65);
`

const FlagsSection = styled.div`
  width: 100%;
`

const FlagsTitle = styled(Title)`
  margin-bottom: 0.75rem !important;
`

const FlagsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const FlagItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 6px;
  margin-bottom: 0.5rem;
`

const SEVERITY_COLORS: Record<'info' | 'warning' | 'error', string> = {
  error: '#ff4d4f',
  warning: '#faad14',
  info: '#1890ff',
}

const SEVERITY_BACKGROUNDS: Record<'info' | 'warning' | 'error', string> = {
  error: 'rgba(255, 77, 79, 0.1)',
  warning: 'rgba(250, 173, 20, 0.1)',
  info: 'rgba(24, 144, 255, 0.1)',
}

const SEVERITY_BORDERS: Record<'info' | 'warning' | 'error', string> = {
  error: 'rgba(255, 77, 79, 0.3)',
  warning: 'rgba(250, 173, 20, 0.3)',
  info: 'rgba(24, 144, 255, 0.3)',
}
const FlagIcon = styled.div<{ $severity: 'info' | 'warning' | 'error' }>`
  font-size: 18px;
  color: ${({ $severity }) =>
    SEVERITY_COLORS[$severity] || SEVERITY_COLORS.info};
  margin-top: 2px;
`

const FlagContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const FlagName = styled(Text)`
  font-size: 14px;
`

const SeverityTag = styled(Tag)<{ $severity: 'info' | 'warning' | 'error' }>`
  width: fit-content;
  text-transform: capitalize;

  color: ${({ $severity }) =>
    SEVERITY_COLORS[$severity] || SEVERITY_COLORS.info};
  background: ${({ $severity }) =>
    SEVERITY_BACKGROUNDS[$severity] || SEVERITY_BACKGROUNDS.info};
  border-color: ${({ $severity }) =>
    SEVERITY_BORDERS[$severity] || SEVERITY_BORDERS.info};
`

const ResetButton = styled(Button)`
  margin-top: 1rem;
`
